import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import styles from './HolidayBuilder.module.scss';

// THE PRODUCT PREVIEW, LARGE. Pressing the preview on the product screen opens
// it here at up to 92% of the viewport's shorter side (Katie, 2026-10-20) --
// on a phone that is the whole width, on a desktop a square the height of the
// window. Desktop and phone share one component; only the square's size moves.
//
// ⚠️ IT RE-RENDERS THE PREVIEW, IT DOES NOT ENLARGE A SCREENSHOT OF IT. The
// caller passes the same `<Item previewOnly …>` the product screen draws, so
// what the customer zooms into is the real composite -- their pose, their
// background, the overlay -- at full resolution, not a scaled-up 420px box.
// The square carries `.flatSheet`, the 1:1 container every pose overlay is
// positioned against on that screen, and the preview is its DIRECT child:
// one wrapper between them and the overlays slide (the Desk Plaque, 2026-09-29).
//
// Dismissal: the close button, the backdrop, or Escape. The page behind it
// stops scrolling while it is open, and focus goes to the close button and
// comes back to whatever opened it, so a keyboard user is never stranded
// behind the overlay. Rendered through a portal so no ancestor's
// `overflow: hidden` or transform can clip or trap it.
export default function PreviewLightbox({
  open, onClose, label, children,
}) {
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const opener = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      if (opener && typeof opener.focus === 'function') opener.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center p-3 font-gift"
      // Inline, not `bg-black/85`: this bundle's Tailwind does not emit the
      // slash-opacity form (the page's own config notes the same for
      // `bg-gift-accent/10`), and the class silently produced no backdrop.
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="preview-lightbox-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className="relative"
      >
        {/* Pinned to the viewport corner, not above the square: the square
            is 92% of the viewport's height on a desktop, which leaves no
            room above it for a button. */}
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close"
          className={'fixed right-3 top-3 flex h-11 w-11 items-center justify-center rounded-gift-pill '
            + 'border-0 text-[28px] leading-none text-white '
            + 'focus:outline-none focus-visible:ring-2 focus-visible:ring-white'}
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }}
        >
          ×
        </button>

        {/* The square: the shorter side of the viewport, less the gutters. */}
        <div
          className={cn(styles.flatSheet, 'overflow-hidden rounded-gift-img bg-gift-tile')}
          style={{ width: 'min(92vw, 92vh)' }}
          data-testid="preview-lightbox-stage"
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

PreviewLightbox.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

PreviewLightbox.defaultProps = {
  children: null,
};
